import type { ReactNode } from 'react';
import { cssColor } from '../colors';
import {
  TASK_PRIORITIES,
  TASK_STATUSES,
  isTaskPriority,
  isTaskStatus,
  priorityClass,
  priorityLabel,
  statusLabel,
} from '../constants';
import { formatDate, isOverdue } from '../format';
import type { Task, UserWithRole } from '../types';
import { ConfirmButton } from './ConfirmButton';

export interface TaskCardProps {
  task: Task;
  users: UserWithRole[];
  canUpdate: boolean;
  canAssign: boolean;
  canDelete: boolean;
  onOpen: (task: Task) => void;
  onPatch: (id: string, patch: { status?: string; priority?: string; assigneeId?: string | null }) => void;
  onDelete: (task: Task) => void;
}

export function TaskCard({
  task,
  users,
  canUpdate,
  canAssign,
  canDelete,
  onOpen,
  onPatch,
  onDelete,
}: TaskCardProps): ReactNode {
  const overdue = isOverdue(task.dueAt, task.status);
  const assignee = task.assigneeId === null ? null : users.find((u) => u.id === task.assigneeId) ?? null;
  const assigneeColor = cssColor(assignee?.role?.color);
  const interactive = canUpdate || canAssign || canDelete;

  return (
    <article className={`task-card ${priorityClass(task.priority)}`}>
      <div
        className="task-card-main"
        role={interactive ? 'button' : undefined}
        tabIndex={interactive ? 0 : undefined}
        onClick={() => onOpen(task)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onOpen(task);
          }
        }}
      >
        <div className="task-card-top">
          <span className={`badge ${priorityClass(task.priority)}`}>
            {priorityLabel(task.priority)}
          </span>
          {task.dueAt !== null && (
            <span className={overdue ? 'badge badge-overdue' : 'badge badge-muted'}>
              {overdue ? 'überfällig · ' : ''}
              {formatDate(task.dueAt)}
            </span>
          )}
        </div>

        <h3 className="task-card-title">{task.title}</h3>

        <div className="task-card-meta">
          {task.assigneeName === null ? (
            <span className="muted small">Nicht zugewiesen</span>
          ) : (
            <span className="task-assignee">
              <span className="user-dot user-dot-small" style={{ backgroundColor: assigneeColor }}>
                {task.assigneeName.slice(0, 1).toUpperCase()}
              </span>
              <span>{task.assigneeName}</span>
            </span>
          )}
        </div>
      </div>

      {interactive && (
        <div className="task-card-controls" onClick={(event) => event.stopPropagation()}>
          {canUpdate && (
            <label className="mini-field">
              <span className="sr-only">Status</span>
              <select
                value={isTaskStatus(task.status) ? task.status : ''}
                onChange={(event) => onPatch(task.id, { status: event.target.value })}
              >
                {!isTaskStatus(task.status) && <option value="">{task.status}</option>}
                {TASK_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {statusLabel(status)}
                  </option>
                ))}
              </select>
            </label>
          )}

          {canUpdate && (
            <label className="mini-field">
              <span className="sr-only">Priorität</span>
              <select
                value={isTaskPriority(task.priority) ? task.priority : ''}
                onChange={(event) => onPatch(task.id, { priority: event.target.value })}
              >
                {!isTaskPriority(task.priority) && <option value="">{task.priority}</option>}
                {TASK_PRIORITIES.map((priority) => (
                  <option key={priority} value={priority}>
                    {priorityLabel(priority)}
                  </option>
                ))}
              </select>
            </label>
          )}

          {canAssign && (
            <label className="mini-field">
              <span className="sr-only">Zuweisung</span>
              <select
                value={task.assigneeId ?? ''}
                onChange={(event) =>
                  onPatch(task.id, {
                    assigneeId: event.target.value === '' ? null : event.target.value,
                  })
                }
              >
                <option value="">Niemand</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.displayName}
                  </option>
                ))}
              </select>
            </label>
          )}

          {canDelete && (
            <ConfirmButton
              label="Löschen"
              question="Aufgabe löschen?"
              onConfirm={() => onDelete(task)}
            />
          )}
        </div>
      )}
    </article>
  );
}
